import type { ReactNode } from 'react'

import AuditOutlined from '@ant-design/icons/AuditOutlined'
import BankOutlined from '@ant-design/icons/BankOutlined'
import DashboardOutlined from '@ant-design/icons/DashboardOutlined'
import DollarCircleOutlined from '@ant-design/icons/DollarCircleOutlined'
import FolderOpenOutlined from '@ant-design/icons/FolderOpenOutlined'
import ToolOutlined from '@ant-design/icons/ToolOutlined'
import UserOutlined from '@ant-design/icons/UserOutlined'

import CollaboratorsIcon from '@public/images/collaborators.png'
import ProjectIcon from '@public/images/project.png'
import SalesmenIcon from '@public/images/salesmen.png'

/**
 * Top-level navigation menu rendered in the Sider.
 *
 * Plain `{ label, path, icon }` list — no CRUD semantics, no link to a
 * "current resource" or to create/edit endpoints. Routes that need a
 * create/edit path embed it themselves. The Sider consumes this list as-is.
 *
 * `key` matches the legacy Refine `resource.name` so existing tests and
 * the temporary `<Refine resources={...}>` derivation in `AppLayout`
 * keep working until the provider is dismantled (W6 of the migration).
 */
export interface NavigationItem {
  key: string
  label: string
  path: string
  icon: ReactNode
}

const ICON_SIZE = 26

export const navigationItems: readonly NavigationItem[] = [
  {
    key: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: <DashboardOutlined style={{ fontSize: `${ICON_SIZE}px` }} />,
  },
  {
    key: 'urban_analysis',
    label: 'Études Urbaines',
    path: '/urban-analysis',
    icon: <AuditOutlined style={{ fontSize: `${ICON_SIZE}px` }} />,
  },
  {
    key: 'projects',
    label: 'Projets',
    path: '/projects',
    icon: (
      <img alt="project" src={ProjectIcon} width={ICON_SIZE} height={ICON_SIZE} />
    ),
  },
  {
    key: 'collaborators',
    label: 'Collaborateurs',
    path: '/collaborators',
    icon: (
      <img
        alt="collaborators"
        src={CollaboratorsIcon}
        width={ICON_SIZE}
        height={ICON_SIZE}
      />
    ),
  },
  {
    key: 'salesmens',
    label: 'Commerciaux',
    path: '/salesmens',
    icon: (
      <img
        alt="salesmens"
        src={SalesmenIcon}
        width={ICON_SIZE}
        height={ICON_SIZE}
      />
    ),
  },
  {
    key: 'companies',
    label: 'Entreprises',
    path: '/companies',
    icon: <BankOutlined style={{ fontSize: `${ICON_SIZE}px` }} />,
  },
  {
    key: 'clients',
    label: 'Clients',
    path: '/clients',
    icon: <UserOutlined style={{ fontSize: `${ICON_SIZE}px` }} />,
  },
  {
    // Readers and collaborators only (#881): the architect manages resources
    // from Outils, so the policy gives it no `resources` entry.
    key: 'resources',
    label: 'Ressources',
    path: '/resources',
    icon: <FolderOpenOutlined style={{ fontSize: `${ICON_SIZE}px` }} />,
  },
  {
    key: 'invoices',
    label: 'Facturation',
    path: '/invoices',
    icon: <DollarCircleOutlined style={{ fontSize: `${ICON_SIZE}px` }} />,
  },
  {
    key: 'tools',
    label: 'Outils',
    path: '/tools',
    icon: <ToolOutlined style={{ fontSize: `${ICON_SIZE}px` }} />,
  },
] as const
